import { useState, useEffect, useRef, useMemo } from "react";
import { createPortal } from "react-dom";
import DraggableModal from "./DraggableModal";
import BulkDeleteDialog from "./BulkDeleteDialog";
import SearchSelect from "./SearchSelect";
import ExportButton from "./ExportButton";
import { num2, intOr, s as xs } from "./xlsxExport";
import { loadFromSupabase, saveToSupabase, deleteFromSupabase } from "../supabaseDB";
import { computeDerivedMonthly, resolveSeasonRange, seasonCycleStartYear, historicalSeasonTotals } from "./seasonComparisonCalc";

/* Profit calculation per season — mirrors ProfitTracker.buildProfitData
   line-for-line. ProfitTracker is the source of truth; the helper there
   isn't exported (and we can't touch that file per spec), so the per-
   LOT logic is duplicated here verbatim and then aggregated by season +
   ISO week.

   Two prior bugs this rewrite fixes vs the old SeasonRegistry calc:

     1. Price-Difference used GROSS salePrice. ProfitTracker subtracts
        the per-line client commission before comparing against the
        reported price (the commission the client deducted never
        reached GFI, so it can't count as price-diff profit).
     2. Commission used the bill's default rate flat. ProfitTracker
        walks each bill line individually and falls back to the bill
        default only when the line lacks its own commissionRate.

   Together those two were ~$82k off on the 2024-25 season per the
   user's verification. */
export function calcSeasonProfits() {
  function loadLS(k, fb) { try { var d = localStorage.getItem(k); return d ? JSON.parse(d) : fb; } catch { return fb; } }
  var bills = loadLS("gfi-bills", []);
  var invoices = loadLS("gfi-invoices", []);
  var shipments = loadLS("gfi-shipments", []);
  var seasons = loadLS("gfi-seasons", []);
  var expenseBills = loadLS("gfi-expense-bills", []);

  var shipByLot = {};
  shipments.forEach(function(s) { if (s.lot) shipByLot[s.lot] = s; });

  /* Index expense bills by lot — store {actualCost, ...} so the lot
     loop below sums them the same way ProfitTracker does. */
  var expenseByLot = {};
  expenseBills.forEach(function(eb) {
    var fees = eb.fees || [];
    var dist = eb.distribution || [];
    var linkedLots = eb.linkedLots || [];
    if (fees.length > 0) {
      fees.forEach(function(fe) {
        if (!fe.shipmentId) return;
        var shp = shipments.find(function(s) { return s.id === fe.shipmentId; });
        var lot = shp ? shp.lot : null;
        if (!lot) return;
        if (!expenseByLot[lot]) expenseByLot[lot] = [];
        expenseByLot[lot].push({ actualCost: parseFloat(fe.amount) || 0 });
      });
      var unlinked = fees.filter(function(fe) { return !fe.shipmentId && parseFloat(fe.amount) > 0; });
      if (unlinked.length > 0 && linkedLots.length > 0) {
        unlinked.forEach(function(fe) {
          var perLot = (parseFloat(fe.amount) || 0) / linkedLots.length;
          linkedLots.forEach(function(sid) { var shp = shipments.find(function(s) { return s.id === sid; }); if (shp && shp.lot) { if (!expenseByLot[shp.lot]) expenseByLot[shp.lot] = []; expenseByLot[shp.lot].push({ actualCost: perLot }); } });
        });
      }
    } else if (dist.length > 0) {
      dist.forEach(function(d) { if (d.lot) { if (!expenseByLot[d.lot]) expenseByLot[d.lot] = []; var pallets = parseInt(d.pallets, 10) || 0; var cost = d.costRate ? (parseFloat(d.costRate) || 0) * pallets : (parseFloat(d.amount) || 0); expenseByLot[d.lot].push({ actualCost: cost }); } });
    } else if (linkedLots.length > 0) {
      var perLot = (parseFloat(eb.amount) || 0) / linkedLots.length;
      linkedLots.forEach(function(sid) { var shp = shipments.find(function(s) { return s.id === sid; }); if (shp && shp.lot) { if (!expenseByLot[shp.lot]) expenseByLot[shp.lot] = []; expenseByLot[shp.lot].push({ actualCost: perLot }); } });
    }
  });

  /* Real per-invoice case rows for one lot — mirrors ProfitTracker.js's
     invoiceRowsForLot fix verbatim (same duplication constraint as the
     rest of this function). Fixes the split-lot profit duplication:
     when a lot is divided across 2+ invoices, each invoice's lines[]
     carries a full copy of the shipment's product lines (the lot's
     TOTAL cases), while invoice.palletAllocations is what actually
     gets edited to reflect the real per-invoice split. Resolve each
     allocation's pallet against the shipment manifest to get its
     product, then match that product (productBase+category+size) to
     the invoice line for salePrice/commissionRate/saleStatus. Legacy
     invoices with no palletAllocations for this lot keep the old
     lines[]-only behavior. */
  function invoiceRowsForLot(inv, lot, shipByLotMap) {
    var allocs = (inv.palletAllocations || []).filter(function(a) { return a && String(a.lot) === String(lot) && (parseInt(a.cases, 10) || 0) > 0; });
    if (allocs.length > 0) {
      var shp = shipByLotMap[lot];
      var manifest = shp ? (shp.palletManifest || []) : [];
      var lines = inv.lines || [];
      var out = [];
      allocs.forEach(function(a) {
        var meta = manifest.find(function(p) { return String(p.palletNum) === String(a.palletNum); }) || null;
        var line = meta && lines.find(function(l) {
          return (l.productBase || l.productName || "") === (meta.productBase || "")
            && (l.category || "") === (meta.category || "")
            && (l.size || "") === (meta.size || "");
        });
        if (!line && meta) {
          line = lines.find(function(l) {
            return (l.productBase || l.productName || "") === (meta.productBase || "")
              && (l.category || "") === (meta.category || "");
          });
        }
        var saleStatus = line ? line.saleStatus : "";
        if (saleStatus === "dumped" || saleStatus === "donated" || saleStatus === "samples" || saleStatus === "transfer") return;
        out.push({
          product: (line && (line.productName || line.productBase)) || (meta && meta.productBase) || "?",
          cases: parseInt(a.cases, 10) || 0,
          salePrice: line ? (parseFloat(line.salePrice) || 0) : 0,
          commissionRate: line ? line.commissionRate : undefined,
        });
      });
      return out;
    }
    return (inv.lines || []).filter(function(l) {
      if (l.lot && l.lot !== lot) return false;
      return l.saleStatus !== "dumped" && l.saleStatus !== "donated" && l.saleStatus !== "samples" && l.saleStatus !== "transfer";
    }).map(function(l) {
      return { product: l.productName || l.productBase || "?", cases: parseInt(l.cases, 10) || 0, salePrice: parseFloat(l.salePrice) || 0, commissionRate: l.commissionRate };
    });
  }

  function getISOWeek(dateStr) {
    if (!dateStr) return "";
    var d = new Date(dateStr + "T00:00:00"); d.setHours(0, 0, 0, 0);
    d.setDate(d.getDate() + 3 - ((d.getDay() + 6) % 7));
    var w1 = new Date(d.getFullYear(), 0, 4);
    return String(1 + Math.round(((d - w1) / 86400000 - 3 + ((w1.getDay() + 6) % 7)) / 7)).padStart(2, "0");
  }
  function findSeason(dateStr) {
    if (!dateStr) return null;
    var d = new Date(dateStr + "T00:00:00");
    return seasons.find(function(s) { return new Date(s.startDate + "T00:00:00") <= d && d <= new Date(s.endEstimate + "T00:00:00"); });
  }

  /* Build the same { lot: { bills, invoices } } map ProfitTracker uses. */
  var lotMap = {};
  bills.forEach(function(b) { if (b.lot) { if (!lotMap[b.lot]) lotMap[b.lot] = { bills: [], invoices: [] }; lotMap[b.lot].bills.push(b); } });
  invoices.forEach(function(inv) {
    var lots = new Set();
    (inv.lines || []).forEach(function(l) { if (l.lot) lots.add(l.lot); });
    if (lots.size === 0 && inv.lot) lots.add(inv.lot);
    lots.forEach(function(lot) { if (!lotMap[lot]) lotMap[lot] = { bills: [], invoices: [] }; lotMap[lot].invoices.push(inv); });
  });

  var seasonProfits = {};
  Object.keys(lotMap).forEach(function(lot) {
    var entry = { lot: lot, bills: lotMap[lot].bills, invoices: lotMap[lot].invoices };
    var shp = shipByLot[lot];
    var dateForSeason = shp ? (shp.dateArrival || shp.dateDeparture || "") : "";
    if (!dateForSeason && entry.bills.length > 0) dateForSeason = entry.bills[0].billDate || "";
    var season = findSeason(dateForSeason);
    if (!season) return;
    if (!seasonProfits[season.id]) seasonProfits[season.id] = { total: 0, weeks: {} };

    var weekNum = getISOWeek(dateForSeason);

    /* 1. PRICE DIFFERENCE — NET salePrice (after per-line client
       commission) vs reportedPrice, summed per product. Per-line
       commissionRate falls back to inv.commissionRate when blank.
       Identical to ProfitTracker.buildProfitData lines 148-167. */
    var repByProd = {}; var saleByProd = {};
    entry.bills.forEach(function(b) { (b.lines || []).forEach(function(l) { var k = l.productName || l.productBase || "?"; var c = parseInt(l.cases, 10) || 0; if (!repByProd[k]) repByProd[k] = 0; repByProd[k] += c * (parseFloat(l.reportedPrice) || 0); }); });
    entry.invoices.forEach(function(inv) {
      var invDefRate = parseFloat(inv.commissionRate) || 0;
      invoiceRowsForLot(inv, entry.lot, shipByLot).forEach(function(row) {
        var k = row.product;
        var lineRate = (row.commissionRate === undefined || row.commissionRate === null || row.commissionRate === "")
          ? invDefRate
          : (parseFloat(row.commissionRate) || 0);
        var netSp = row.salePrice * (1 - lineRate / 100);
        if (!saleByProd[k]) saleByProd[k] = 0;
        saleByProd[k] += row.cases * netSp;
      });
    });
    var lotPD = 0;
    new Set([...Object.keys(repByProd), ...Object.keys(saleByProd)]).forEach(function(k) { lotPD += (saleByProd[k] || 0) - (repByProd[k] || 0); });

    /* 2. COMMISSION — per-line commissionRate × line gross (cases ×
       reportedPrice), falling back to the bill default when the line
       lacks a rate. Identical to ProfitTracker lines 173-184. */
    var lotComm = 0;
    entry.bills.forEach(function(b) {
      var billDefRate = parseFloat(b.commissionRate) || 0;
      (b.lines || []).forEach(function(l) {
        if (l.saleStatus === "dumped" || l.saleStatus === "donated" || l.saleStatus === "samples" || l.saleStatus === "transfer") return;
        var lineAmt = (parseInt(l.cases, 10) || 0) * (parseFloat(l.reportedPrice) || 0);
        var lineRate = (l.commissionRate === undefined || l.commissionRate === null || l.commissionRate === "")
          ? billDefRate
          : (parseFloat(l.commissionRate) || 0);
        lotComm += lineAmt * (lineRate / 100);
      });
    });

    /* 3. FEE MARKUP — bill fees minus expense bill costs, excluding
       Advance Financing Fee (handled separately as Finance Markup). */
    var billFees = 0; var financeCharged = 0;
    entry.bills.forEach(function(b) { (b.fees || []).forEach(function(fe) { if (fe.passThrough) return; var amt = parseFloat(fe.amount) || 0; var fname = fe.feeType === "__custom__" ? fe.feeCustom : (fe.feeType || "Other"); if (fname === "Advance Financing Fee") { financeCharged += amt; } else { billFees += amt; } }); });
    var actualCosts = 0;
    (expenseByLot[lot] || []).forEach(function(exp) { actualCosts += exp.actualCost; });
    var lotFM = billFees - actualCosts;

    /* 4. FINANCE MARKUP — invoice's Advance Financing Fee minus the
       slice of cost attributable to this lot. Same lot-routing logic
       as ProfitTracker lines 196-219: prefer transferTargetBillId,
       fall through to single-lot invoice match, multi-lot prorate by
       cases, or count when no target bill is found at all. */
    var financeCost = 0;
    var allBillsForFin = loadLS("gfi-bills", []);
    entry.invoices.forEach(function(inv) {
      (inv.fees || []).forEach(function(fe) {
        var fname = fe.feeType === "__custom__" ? fe.feeCustom : (fe.feeType || "");
        if (fname !== "Advance Financing Fee") return;
        var amt = parseFloat(fe.amount) || 0;
        if (amt === 0) return;
        if (fe.transferTargetBillId) {
          var tb = allBillsForFin.find(function(b) { return b.id === fe.transferTargetBillId; });
          if (tb && tb.lot === entry.lot) financeCost += amt;
          else if (tb && tb.lot !== entry.lot) return;
          else financeCost += amt;
        } else if (!inv.lot || inv.lot === entry.lot) {
          var invLots = new Set(); (inv.lines || []).forEach(function(l) { if (l.lot) invLots.add(l.lot); });
          if (invLots.size <= 1) financeCost += amt;
          else {
            var tC = 0, lC = 0;
            (inv.lines || []).forEach(function(l) { var cs = parseInt(l.cases, 10) || 0; tC += cs; if (!l.lot || l.lot === entry.lot) lC += cs; });
            financeCost += amt * (tC > 0 ? lC / tC : 0);
          }
        }
      });
    });
    var lotFinance = financeCharged - financeCost;

    var lotProfit = lotPD + lotComm + lotFM + lotFinance;

    /* Cases — invoice first, bill fallback when no invoice cases. */
    var lotCases = 0;
    entry.invoices.forEach(function(inv) { invoiceRowsForLot(inv, entry.lot, shipByLot).forEach(function(row) { lotCases += row.cases; }); });
    if (lotCases === 0) { entry.bills.forEach(function(b) { (b.lines || []).forEach(function(l) { lotCases += parseInt(l.cases, 10) || 0; }); }); }

    if (weekNum) {
      if (!seasonProfits[season.id].weeks[weekNum]) seasonProfits[season.id].weeks[weekNum] = { week: weekNum, cases: 0, profit: 0, lots: [] };
      seasonProfits[season.id].weeks[weekNum].cases += lotCases;
      seasonProfits[season.id].weeks[weekNum].profit += lotProfit;
      seasonProfits[season.id].weeks[weekNum].lots.push(lot);
    }
  });

  Object.keys(seasonProfits).forEach(function(sid) {
    var sp = seasonProfits[sid];
    sp.total = Object.values(sp.weeks).reduce(function(s, w) { return s + w.profit; }, 0);
    sp.weekCount = Object.keys(sp.weeks).length;
    sp.weeklyReal = sp.weekCount > 0 ? sp.total / sp.weekCount : 0;
    sp.weekDetail = Object.values(sp.weeks).sort(function(a, b) { return a.week.localeCompare(b.week); });
  });
  return seasonProfits;
}

/* Single source of truth for "Profit Real" + "Cases" per season, used
   by the list rows, the aggregate() totals (cards/TOTAL row), the sort
   comparator, and the SeasonDetail prop builder — so all four stay
   consistent instead of drifting.

   A Closed season captured via the Historical Monthly tab has no
   linked shipments/bills/invoices for calcSeasonProfits' live path to
   find, so it always came back as 0 there. historicalSeasonTotals sums
   the manually-captured monthlyHistorical directly; only Closed
   seasons that actually have captured data take this branch. Open
   seasons, and Closed seasons with no Historical Monthly data at all
   (nothing captured yet), keep the exact existing calcSeasonProfits-
   based behavior — unchanged, per spec. weekCount/weekDetail stay
   whatever calcSeasonProfits produced (empty for historical-only
   seasons) since the weekly breakdown itself is explicitly out of
   scope for this fix. */
function getSeasonTotals(s, seasonProfits) {
  if (s.status === "closed") {
    var hist = historicalSeasonTotals(s);
    if (hist) return { profit: hist.profit, cases: hist.cases, weekCount: 0, weekDetail: [] };
  }
  var sp = seasonProfits[s.id] || {};
  var cases = (sp.weekDetail || []).reduce(function(sum, w) { return sum + w.cases; }, 0);
  return { profit: sp.total || 0, cases: cases, weekCount: sp.weekCount || 0, weekDetail: sp.weekDetail || [] };
}

const T = {
  en: {
    title: "Season Registry", add: "New season", edit: "Edit season",
    showing: "Showing", of: "of", total: "seasons", noResults: "No seasons found",
    // Fields
    seasonName: "Season name", autoGenerated: "Auto-generated from dates",
    startDate: "Start date", endEstimate: "Estimated end date", endEstimateSub: "Adjustable during the season",
    preCloseDate: "Pre-close date", preCloseSub: "No new shipments, existing ones still adjustable",
    closeDate: "Close date", closeSub: "All settled, no modifications allowed",
    profitGoal: "Profit goal (USD)", profitGoalSub: "Suggested $250,000 for cash flow coverage (any amount allowed)",
    weeks: "Weeks", weeklyTarget: "Weekly target",
    // Status
    status: "Status", open: "Open", preClose: "Pre-close", closed: "Closed", reOpened: "Re-opened",
    // Actions
    openSeason: "Open Season", preCloseSeason: "Pre-close", closeSeason: "Close Season", reOpenSeason: "Re-open",
    confirmPreClose: "Mark this season as pre-closed? No new shipments will be accepted, but existing ones can still be modified.",
    confirmClose: "Close this season? All shipments and accounts must be settled. No modifications will be allowed.",
    confirmReOpen: "Re-open this season? This will allow modifications to shipments and sales.",
    // Form
    save: "Save", cancel: "Cancel", delete: "Delete", editBtn: "Edit", close: "Close",
    confirmDel: "Delete this season?", yes: "Yes", no: "No",
    confirmExit: "You have unsaved changes. Exit without saving?", yesExit: "Yes, exit", noExit: "Keep editing",
    saved: "Season saved", deleted: "Season deleted",
    required: "Required", fillRequired: "Please fill all required fields",
    minProfit: "Minimum profit goal is $250,000",
    overlapError: "Dates overlap with another season",
    alreadyOpen: "There is already an open season. Close or pre-close it first.",
    // Detail
    duration: "Duration", startWeek: "Start week", endWeek: "End week",
    accumulated: "Accumulated profit", remaining: "Remaining to goal",
    projection: "Projection", onTrack: "On track", behind: "Behind", ahead: "Ahead",
    profitReal: "Profit (Real)", weeklyReal: "Weekly (Real)",
    noData: "No shipment data yet",
    search: "Search by name...", allStatuses: "All statuses", sortBy: "Sort by",
    totalProfit: "Total Profit", totalCases: "Total Cases", profitPerBox: "Profit per Box", allSeasons: "All seasons", totalRow: "TOTAL",
    // Historical Monthly tab (Season Comparison feature)
    tabDetails: "Details", tabHistorical: "Historical Monthly",
    historicalBanner: "Enter historical monthly data for this season — fill in Cases and Profit for the months it was active.",
    colMonth: "Month", colCases: "Cases", colProfit: "Profit", colAcumulado: "Acumulated", colProfitBox: "Profit/box", colProfitDay: "Profit/day", colProfitHour: "Profit/hour",
    months: ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"],
  },
  es: {
    title: "Registro de Temporadas", add: "Nueva temporada", edit: "Editar temporada",
    showing: "Mostrando", of: "de", total: "temporadas", noResults: "No se encontraron temporadas",
    seasonName: "Nombre de temporada", autoGenerated: "Auto-generado por fechas",
    startDate: "Fecha de inicio", endEstimate: "Fecha estimada de cierre", endEstimateSub: "Ajustable durante la temporada",
    preCloseDate: "Fecha de pre-cierre", preCloseSub: "Sin embarques nuevos, los existentes aún se pueden ajustar",
    closeDate: "Fecha de cierre", closeSub: "Todo saldado, sin modificaciones",
    profitGoal: "Meta de ganancia (USD)", profitGoalSub: "Sugerido $250,000 para cubrir meses sin actividad (cualquier monto permitido)",
    weeks: "Semanas", weeklyTarget: "Meta semanal",
    status: "Estado", open: "Abierta", preClose: "Pre-cierre", closed: "Cerrada", reOpened: "Re-abierta",
    openSeason: "Abrir Temporada", preCloseSeason: "Pre-cierre", closeSeason: "Cerrar Temporada", reOpenSeason: "Re-abrir",
    confirmPreClose: "¿Marcar esta temporada como pre-cierre? No se aceptarán embarques nuevos, pero los existentes se pueden modificar.",
    confirmClose: "¿Cerrar esta temporada? Todos los embarques y cuentas deben estar saldados. No se permitirán modificaciones.",
    confirmReOpen: "¿Re-abrir esta temporada? Esto permitirá modificaciones a embarques y ventas.",
    save: "Guardar", cancel: "Cancelar", delete: "Eliminar", editBtn: "Editar", close: "Cerrar",
    confirmDel: "¿Eliminar esta temporada?", yes: "Sí", no: "No",
    confirmExit: "Tienes cambios sin guardar. ¿Salir sin guardar?", yesExit: "Sí, salir", noExit: "Seguir editando",
    saved: "Temporada guardada", deleted: "Temporada eliminada",
    required: "Requerido", fillRequired: "Por favor llena todos los campos requeridos",
    minProfit: "La meta mínima de ganancia es $250,000",
    overlapError: "Las fechas se solapan con otra temporada",
    alreadyOpen: "Ya existe una temporada abierta. Ciérrala o pre-ciérrala primero.",
    duration: "Duración", startWeek: "Semana inicio", endWeek: "Semana fin",
    accumulated: "Ganancia acumulada", remaining: "Restante para meta",
    projection: "Proyección", onTrack: "En camino", behind: "Por debajo", ahead: "Por encima",
    profitReal: "Ganancia (Real)", weeklyReal: "Semanal (Real)",
    noData: "Sin datos de embarques aún",
    search: "Buscar por nombre...", allStatuses: "Todos los estados", sortBy: "Ordenar por",
    totalProfit: "Ganancia Total", totalCases: "Cajas Totales", profitPerBox: "Ganancia por Caja", allSeasons: "Todas las temporadas", totalRow: "TOTAL",
    tabDetails: "Detalles", tabHistorical: "Histórico Mensual",
    historicalBanner: "Captura los datos históricos mensuales de esta temporada — llena Cajas y Ganancia de los meses en que estuvo activa.",
    colMonth: "Mes", colCases: "Cajas", colProfit: "Ganancia", colAcumulado: "Acumulado", colProfitBox: "Gan./caja", colProfitDay: "Gan./día", colProfitHour: "Gan./hora",
    months: ["Ene", "Feb", "Mar", "Abr", "May", "Jun", "Jul", "Ago", "Sep", "Oct", "Nov", "Dic"],
  },
};

/* status defaults to "closed" — most new-season creations now come
   from historical capture (past years being backfilled), not from
   starting the next live operating season. The user explicitly picks
   "open" via the Status field below when it IS the next real season. */
const EMPTY = { startDate: "", endEstimate: "", preCloseDate: "", closeDate: "", profitGoal: 250000, status: "closed" };
const inp = { padding: "8px 10px", fontSize: 13, border: "1px solid var(--color-border)", borderRadius: 6, background: "var(--color-bg-input)", color: "var(--color-text)", outline: "none", transition: "all 0.2s", fontFamily: "inherit", width: "100%", boxSizing: "border-box" };
/* Brushed Steel "recessed field on a plate" treatment — duplicated
   per-file from ShipmentRegistry.js's filterFieldPlate, same
   convention as Bills/Invoices (never a shared import). Passed to
   every SearchSelect/date-input inside the Seasons Filters popover. */
var filterFieldPlate = {
  background: "linear-gradient(180deg, #f2f3f5 0%, #d7dadd 100%)",
  border: "1px solid #9aa0a8",
  borderRadius: 0,
  boxShadow: "inset 0 1px 2px rgba(0,0,0,0.15), inset 0 -1px 0 rgba(255,255,255,0.6)",
  minHeight: 25,
};

function Field({ label, required, error, sub, children }) { return (<div style={{ display: "flex", flexDirection: "column", gap: 4 }}><label style={{ fontSize: 12, fontWeight: 500, color: "var(--color-text-secondary)", display: "flex", alignItems: "center", gap: 4 }}>{label}{required && <span style={{ color: "var(--color-danger)", fontSize: 10 }}>*</span>}</label>{children}{sub && <span style={{ fontSize: 10, color: "var(--color-text-muted)", fontStyle: "italic" }}>{sub}</span>}{error && <span style={{ fontSize: 11, color: "var(--color-danger)" }}>{error}</span>}</div>); }
function Section({ title, children }) { return (<div style={{ display: "flex", flexDirection: "column", gap: 12 }}><div style={{ fontSize: 11, fontWeight: 600, color: "var(--color-text-muted)", textTransform: "uppercase", letterSpacing: "0.06em", borderBottom: "1px solid var(--color-border)", paddingBottom: 4 }}>{title}</div>{children}</div>); }
function Toast({ message, onClose }) {
  const [fading, setFading] = useState(false);
  useEffect(() => {
    const fadeT = setTimeout(() => setFading(true), 2700);
    const closeT = setTimeout(() => { onClose(); }, 3000);
    return () => { clearTimeout(fadeT); clearTimeout(closeT); };
  }, [message]);
  return createPortal(<div style={{ position: "fixed", bottom: 24, left: "50%", transform: "translateX(-50%)", background: "var(--color-accent)", color: "var(--color-accent-text)", padding: "10px 20px", borderRadius: 8, fontSize: 13, fontWeight: 500, zIndex: 999999, boxShadow: "0 8px 24px var(--color-shadow)", animation: fading ? "fadeOut 0.3s ease forwards" : "fadeIn 0.2s ease" }}>{message}</div>, document.body);
}

function genName(start, end) {
  if (!start) return "";
  const sy = new Date(start).getFullYear();
  if (!end) return String(sy);
  const ey = new Date(end).getFullYear();
  return sy === ey ? String(sy) : sy + "-" + ey;
}

export function calcWeeks(start, end) {
  if (!start || !end) return 0;
  const ms = new Date(end) - new Date(start);
  return Math.max(1, Math.round(ms / (7 * 24 * 60 * 60 * 1000)));
}

function getWeekNum(dateStr) {
  if (!dateStr) return "—";
  const d = new Date(dateStr);
  const oneJan = new Date(d.getFullYear(), 0, 1);
  return Math.ceil(((d - oneJan) / 86400000 + oneJan.getDay() + 1) / 7);
}

function fmtMoney(n) { return "$" + Number(n || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 }); }

function calcWeeksElapsed(startDate) {
  if (!startDate) return 0;
  const now = new Date(); const start = new Date(startDate);
  if (now < start) return 0;
  const ms = now - start;
  return Math.max(1, Math.round(ms / (7 * 24 * 60 * 60 * 1000)));
}

const STATUS_STYLES = {
  open: { bg: "rgba(16,185,129,0.08)", text: "#059669", border: "rgba(16,185,129,0.25)" },
  preClose: { bg: "rgba(245,158,11,0.08)", text: "#D97706", border: "rgba(245,158,11,0.25)" },
  closed: { bg: "rgba(107,114,128,0.08)", text: "#6B7280", border: "rgba(107,114,128,0.25)" },
  reOpened: { bg: "rgba(37,99,235,0.08)", text: "#2563EB", border: "rgba(37,99,235,0.25)" },
};
/* 2026-10-21, Arturo: "matea toda la app" — same rectangular/3D-bevel
   recipe as ShipmentRegistry.js's TEXAS_QC_BEVEL, applied to the
   Open/Pre-Close/Closed/Re-opened season status badge. */
const STATUS_BEVEL = "inset 0 1px 0 rgba(255,255,255,0.6), inset 0 -1px 2px rgba(0,0,0,0.12), 0 1px 2px rgba(0,0,0,0.12)";

/* ===== DETAIL ===== */
function SeasonDetail({ season, t, lang, onEdit, onClose, onStatusChange }) {
  const [confirm, setConfirm] = useState(null);
  const name = genName(season.startDate, season.endEstimate);
  const weeks = calcWeeks(season.startDate, season.endEstimate);
  const weeklyTarget = weeks > 0 ? season.profitGoal / weeks : 0;
  const ss = STATUS_STYLES[season.status] || STATUS_STYLES.open;
  const statusLabel = t[season.status] || season.status;
  const canPreClose = season.status === "open" || season.status === "reOpened";
  const canClose = season.status === "preClose";
  const canReOpen = season.status === "closed";

  return (
    <div style={{ position: "fixed", inset: 0, background: "var(--color-bg-modal)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000, padding: 16 }}>
      <div style={{ background: "var(--color-bg-card-solid)", borderRadius: 16, width: "100%", maxWidth: 520, maxHeight: "90vh", overflow: "auto", border: "1px solid var(--color-border)", animation: "slideIn 0.2s ease", boxShadow: "0 12px 40px var(--color-shadow)" }}>
        <div style={{ padding: "18px 22px 14px", borderBottom: "1px solid var(--color-border)", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <div>
            <h2 style={{ margin: 0, fontSize: 18, fontWeight: 700, color: "var(--color-text)" }}>{name}</h2>
            <span style={{ padding: "3px 10px", borderRadius: 0, fontSize: 10, fontWeight: 700, background: ss.bg, color: ss.text, border: `1px solid ${ss.border}`, boxShadow: STATUS_BEVEL, marginTop: 4, display: "inline-block" }}>{statusLabel}</span>
          </div>
          <button onClick={onClose} style={{ background: "none", border: "none", fontSize: 20, cursor: "pointer", color: "var(--color-text-muted)", padding: "0 4px", lineHeight: 1 }}>×</button>
        </div>
        <div style={{ padding: "18px 22px", display: "flex", flexDirection: "column", gap: 18 }}>
          <Section title={t.duration}>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12 }}>
              <div style={{ textAlign: "center", background: "var(--color-bg)", borderRadius: 10, padding: "12px 8px" }}>
                <div style={{ fontFamily: "'JetBrains Mono', monospace", fontWeight: 700, fontSize: 22, color: "var(--color-accent)" }}>{weeks}</div>
                <div style={{ fontSize: 11, color: "var(--color-text-muted)", marginTop: 2 }}>{t.weeks}</div>
              </div>
              <div style={{ textAlign: "center", background: "var(--color-bg)", borderRadius: 10, padding: "12px 8px" }}>
                <div style={{ fontFamily: "'JetBrains Mono', monospace", fontWeight: 700, fontSize: 16, color: "var(--color-text)" }}>W{getWeekNum(season.startDate)}</div>
                <div style={{ fontSize: 11, color: "var(--color-text-muted)", marginTop: 2 }}>{t.startWeek}</div>
              </div>
              <div style={{ textAlign: "center", background: "var(--color-bg)", borderRadius: 10, padding: "12px 8px" }}>
                <div style={{ fontFamily: "'JetBrains Mono', monospace", fontWeight: 700, fontSize: 16, color: "var(--color-text)" }}>W{getWeekNum(season.endEstimate)}</div>
                <div style={{ fontSize: 11, color: "var(--color-text-muted)", marginTop: 2 }}>{t.endWeek}</div>
              </div>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "6px 20px", fontSize: 13, marginTop: 4 }}>
              <div><span style={{ color: "var(--color-text-muted)" }}>{t.startDate}:</span> <strong>{season.startDate}</strong></div>
              <div><span style={{ color: "var(--color-text-muted)" }}>{t.endEstimate}:</span> <strong>{season.endEstimate}</strong></div>
              {season.preCloseDate && <div><span style={{ color: "var(--color-text-muted)" }}>{t.preCloseDate}:</span> <strong>{season.preCloseDate}</strong></div>}
              {season.closeDate && <div><span style={{ color: "var(--color-text-muted)" }}>{t.closeDate}:</span> <strong>{season.closeDate}</strong></div>}
            </div>
          </Section>

          <Section title={t.profitGoal}>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
              <div style={{ textAlign: "center", background: "var(--color-bg)", borderRadius: 10, padding: "12px 8px" }}>
                <div style={{ fontFamily: "'JetBrains Mono', monospace", fontWeight: 700, fontSize: 20, color: "var(--color-accent)" }}>{fmtMoney(season.profitGoal)}</div>
                <div style={{ fontSize: 11, color: "var(--color-text-muted)", marginTop: 2 }}>{t.profitGoal}</div>
              </div>
              <div style={{ textAlign: "center", background: "var(--color-bg)", borderRadius: 10, padding: "12px 8px" }}>
                <div style={{ fontFamily: "'JetBrains Mono', monospace", fontWeight: 700, fontSize: 20, color: "var(--color-text)" }}>{fmtMoney(weeklyTarget)}</div>
                <div style={{ fontSize: 11, color: "var(--color-text-muted)", marginTop: 2 }}>{t.weeklyTarget}</div>
              </div>
            </div>
            {season.profitReal > 0 ? (
              <div>
                {(function() {
                  /* casesTotal (set by the parent via getSeasonTotals) takes
                     precedence — a Historical Monthly-captured season has no
                     weekDetail to sum (no linked shipments), so falling back
                     to that sum alone would silently zero out Profit/box
                     again even after profitReal itself got fixed. */
                  var totalCases = season.casesTotal != null ? season.casesTotal : (season.weekDetail || []).reduce(function(s, w) { return s + w.cases; }, 0);
                  var perBox = totalCases > 0 ? season.profitReal / totalCases : 0;
                  return <div>
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 10, marginTop: 10 }}>
                      <div style={{ textAlign: "center", background: "var(--color-bg)", borderRadius: 10, padding: "14px 8px" }}>
                        <div style={{ fontFamily: "'JetBrains Mono', monospace", fontWeight: 700, fontSize: 20, color: season.profitReal >= 0 ? "#059669" : "#DC2626" }}>{fmtMoney(season.profitReal)}</div>
                        <div style={{ fontSize: 11, color: "var(--color-text-muted)", marginTop: 2 }}>{t.accumulated}</div>
                      </div>
                      <div style={{ textAlign: "center", background: "var(--color-bg)", borderRadius: 10, padding: "14px 8px" }}>
                        <div style={{ fontFamily: "'JetBrains Mono', monospace", fontWeight: 700, fontSize: 20, color: (season.profitGoal - season.profitReal) > 0 ? "var(--color-text)" : "#059669" }}>{fmtMoney(Math.max(0, season.profitGoal - season.profitReal))}</div>
                        <div style={{ fontSize: 11, color: "var(--color-text-muted)", marginTop: 2 }}>{t.remaining}</div>
                      </div>
                      <div style={{ textAlign: "center", background: "var(--color-bg)", borderRadius: 10, padding: "14px 8px" }}>
                        <div style={{ fontFamily: "'JetBrains Mono', monospace", fontWeight: 700, fontSize: 20, color: "var(--color-accent)" }}>{fmtMoney(season.weekCount > 0 ? season.profitReal / season.weekCount : 0)}</div>
                        <div style={{ fontSize: 11, color: "var(--color-text-muted)", marginTop: 2 }}>{t.weeklyReal} ({season.weekCount} {lang === "es" ? "sem." : "wks"})</div>
                      </div>
                    </div>
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginTop: 8 }}>
                      <div style={{ textAlign: "center", background: "var(--color-bg)", borderRadius: 10, padding: "14px 8px" }}>
                        <div style={{ fontFamily: "'JetBrains Mono', monospace", fontWeight: 700, fontSize: 20, color: "var(--color-text)" }}>{totalCases.toLocaleString()}</div>
                        <div style={{ fontSize: 11, color: "var(--color-text-muted)", marginTop: 2 }}>{lang === "es" ? "Total Cajas" : "Total Cases"}</div>
                      </div>
                      <div style={{ textAlign: "center", background: "rgba(42,122,111,0.06)", border: "1px solid rgba(42,122,111,0.15)", borderRadius: 10, padding: "14px 8px" }}>
                        <div style={{ fontFamily: "'JetBrains Mono', monospace", fontWeight: 700, fontSize: 20, color: "var(--color-accent)" }}>{fmtMoney(perBox)}</div>
                        <div style={{ fontSize: 11, color: "var(--color-text-muted)", marginTop: 2 }}>{lang === "es" ? "Ganancia/Caja" : "Profit/Box"}</div>
                      </div>
                    </div>
                  </div>;
                })()}
                {/* Weekly breakdown table */}
                {(season.weekDetail || []).length > 0 && (
                  <div style={{ marginTop: 14 }}>
                    <div style={{ fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em", color: "var(--color-text-muted)", marginBottom: 6 }}>{lang === "es" ? "Detalle por Semana" : "Weekly Detail"}</div>
                    <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 11 }}>
                      <thead><tr style={{ borderBottom: "2px solid var(--color-border)" }}>
                        <th style={{ padding: "6px 8px", textAlign: "left", fontSize: 9, color: "var(--color-text-muted)", fontWeight: 700 }}>{t.weeks}</th>
                        <th style={{ padding: "6px 8px", textAlign: "right", fontSize: 9, color: "var(--color-text-muted)", fontWeight: 700 }}>{lang === "es" ? "Cajas" : "Cases"}</th>
                        <th style={{ padding: "6px 8px", textAlign: "right", fontSize: 9, color: "var(--color-text-muted)", fontWeight: 700 }}>{lang === "es" ? "Ganancia" : "Profit"}</th>
                        <th style={{ padding: "6px 8px", textAlign: "left", fontSize: 9, color: "var(--color-text-muted)", fontWeight: 700 }}>{lang === "es" ? "Lotes" : "Lots"}</th>
                      </tr></thead>
                      <tbody>{(season.weekDetail || []).map(function(wd) {
                        return <tr key={wd.week} style={{ borderBottom: "1px solid var(--color-border)" }}>
                          <td style={{ padding: "6px 8px", fontFamily: "'JetBrains Mono', monospace", fontWeight: 600 }}>W{wd.week}</td>
                          <td style={{ padding: "6px 8px", textAlign: "right", fontFamily: "'JetBrains Mono', monospace" }}>{wd.cases.toLocaleString()}</td>
                          <td style={{ padding: "6px 8px", textAlign: "right", fontFamily: "'JetBrains Mono', monospace", fontWeight: 700, color: wd.profit >= 0 ? "#059669" : "#DC2626" }}>{fmtMoney(wd.profit)}</td>
                          <td style={{ padding: "6px 8px", fontFamily: "'JetBrains Mono', monospace", fontSize: 10, color: "var(--color-text-muted)" }}>{wd.lots.join(", ")}</td>
                        </tr>;
                      })}</tbody>
                    </table>
                  </div>
                )}
              </div>
            ) : (
              <div style={{ textAlign: "center", fontSize: 12, color: "var(--color-text-muted)", fontStyle: "italic", marginTop: 4 }}>{t.noData}</div>
            )}
          </Section>

          {/* Status actions */}
          {(canPreClose || canClose || canReOpen) && <Section title={t.status}>
            {!confirm && <div style={{ display: "flex", gap: 8 }}>
              {canPreClose && <button onClick={() => setConfirm("preClose")} style={{ flex: 1, padding: "10px 16px", borderRadius: 8, border: "1px solid rgba(245,158,11,0.3)", background: "rgba(245,158,11,0.06)", color: "#D97706", cursor: "pointer", fontSize: 12, fontWeight: 600 }}>{t.preCloseSeason}</button>}
              {canClose && <button onClick={() => setConfirm("close")} style={{ flex: 1, padding: "10px 16px", borderRadius: 8, border: "1px solid rgba(107,114,128,0.3)", background: "rgba(107,114,128,0.06)", color: "#6B7280", cursor: "pointer", fontSize: 12, fontWeight: 600 }}>{t.closeSeason}</button>}
              {canReOpen && <button onClick={() => setConfirm("reOpen")} style={{ flex: 1, padding: "10px 16px", borderRadius: 8, border: "1px solid rgba(37,99,235,0.3)", background: "rgba(37,99,235,0.06)", color: "#2563EB", cursor: "pointer", fontSize: 12, fontWeight: 600 }}>{t.reOpenSeason}</button>}
            </div>}
            {confirm && <div style={{ background: "rgba(245,158,11,0.06)", border: "1px solid rgba(245,158,11,0.2)", borderRadius: 8, padding: 12, display: "flex", flexDirection: "column", gap: 8 }}>
              <span style={{ fontSize: 12, color: "var(--color-text-secondary)" }}>{confirm === "preClose" ? t.confirmPreClose : confirm === "close" ? t.confirmClose : t.confirmReOpen}</span>
              <div style={{ display: "flex", gap: 6 }}>
                <button onClick={() => { onStatusChange(season.id, confirm === "preClose" ? "preClose" : confirm === "close" ? "closed" : "reOpened"); setConfirm(null); }} style={{ padding: "6px 14px", borderRadius: 6, border: "none", background: "var(--color-accent)", color: "var(--color-accent-text)", cursor: "pointer", fontSize: 12, fontWeight: 600 }}>{t.yes}</button>
                <button onClick={() => setConfirm(null)} style={{ padding: "6px 14px", borderRadius: 6, border: "1px solid var(--color-border)", background: "transparent", color: "var(--color-text-secondary)", cursor: "pointer", fontSize: 12 }}>{t.no}</button>
              </div>
            </div>}
          </Section>}
        </div>
        <div style={{ padding: "14px 22px 18px", borderTop: "1px solid var(--color-border)", display: "flex", justifyContent: "flex-end", gap: 8 }}>
          <button onClick={onClose} style={{ padding: "8px 16px", borderRadius: 6, border: "1px solid var(--color-border)", background: "transparent", color: "var(--color-text-secondary)", cursor: "pointer", fontSize: 13, fontWeight: 500 }}>{t.close}</button>
          {/* Edit is reachable for Closed seasons too — Historical
              Monthly capture/editing must stay available after close.
              (Previously hidden entirely for status==="closed", which
              made the whole form — not just the historical tab —
              unreachable once a season was saved as Closed.) */}
          <button onClick={() => onEdit(season)} style={{ padding: "8px 20px", borderRadius: 6, border: "none", background: "var(--color-accent)", color: "var(--color-accent-text)", cursor: "pointer", fontSize: 13, fontWeight: 600 }}>{t.editBtn}</button>
        </div>
      </div>
    </div>
  );
}

/* ===== HISTORICAL MONTHLY TAB (Season Comparison feature) =====
   Manual capture for Closed seasons: only Month/Cases/Profit are user
   input, the other 4 columns (Acumulado, Profit/box, Profit/day,
   Profit/hour) are computed live via seasonComparisonCalc — the user
   never edits them directly, per spec. No linked shipments exist for
   historical entry, so the business-day range falls back to
   season.startDate/closeDate (resolveSeasonRange's fallback path). */
var histTh = { padding: "6px 8px", textAlign: "left", fontWeight: 700, fontSize: 10, textTransform: "uppercase", letterSpacing: "0.05em", color: "var(--color-text-secondary)", borderBottom: "2px solid var(--color-border)" };
var histThR = { ...histTh, textAlign: "right" };
var histTd = { padding: "4px 6px", fontSize: 12 };
var histTdR = { ...histTd, textAlign: "right", fontFamily: "'JetBrains Mono', monospace" };
var histInp = { padding: "5px 8px", fontSize: 12, border: "1px solid var(--color-border)", borderRadius: 5, background: "var(--color-bg-input)", color: "var(--color-text)", outline: "none", width: "100%", boxSizing: "border-box", fontFamily: "'JetBrains Mono', monospace" };

function HistoricalMonthlyTab({ form, set, t }) {
  var rows = Array.isArray(form.monthlyHistorical) ? form.monthlyHistorical : [];
  var byMonth = {};
  rows.forEach(function (r) { if (r && r.month) byMonth[r.month] = r; });
  var hasAnyData = rows.some(function (r) { return r && ((r.cases !== "" && r.cases != null) || (r.profit !== "" && r.profit != null)); });

  var seasonRange = resolveSeasonRange(form, []);
  var startYear = seasonCycleStartYear(form);
  var baseRows = [];
  for (var m = 1; m <= 12; m++) baseRows.push(byMonth[m] || { month: m, cases: "", profit: "" });
  /* computeDerivedMonthly returns all 12 rows in the canonical Aug->Jul
     operating-cycle order (SEASON_MONTH_ORDER), not calendar 1->12 —
     the .map() below renders them in whatever order they come back in,
     so no separate reordering is needed here. */
  var derived = computeDerivedMonthly(baseRows, startYear, seasonRange);

  var updateRow = function (month, field, value) {
    var next = [];
    for (var mm = 1; mm <= 12; mm++) {
      var existing = byMonth[mm] || { month: mm, cases: "", profit: "" };
      next.push(mm === month ? { ...existing, [field]: value } : existing);
    }
    set("monthlyHistorical", next);
  };

  var fmtOrDash = function (v) { return v === null || v === undefined ? "—" : fmtMoney(v); };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
      {!hasAnyData && (
        <div style={{ padding: "10px 14px", borderRadius: 8, background: "rgba(37,99,235,0.06)", border: "1px solid rgba(37,99,235,0.2)", color: "#2563EB", fontSize: 12, fontWeight: 500 }}>
          {t.historicalBanner}
        </div>
      )}
      <div style={{ overflowX: "auto" }}>
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead>
            <tr>
              <th style={histTh}>{t.colMonth}</th>
              <th style={histTh}>{t.colCases}</th>
              <th style={histTh}>{t.colProfit}</th>
              <th style={histThR}>{t.colAcumulado}</th>
              <th style={histThR}>{t.colProfitBox}</th>
              <th style={histThR}>{t.colProfitDay}</th>
              <th style={histThR}>{t.colProfitHour}</th>
            </tr>
          </thead>
          <tbody>
            {derived.map(function (d) {
              /* Inputs bind to the RAW stored string (byMonth), never to
                 `d.cases`/`d.profit` — those are post-Number()-coercion
                 values from computeDerivedMonthly. Binding a controlled
                 input to the coerced value round-trips every keystroke
                 through Number(), and Number("-") is NaN: typing just
                 "-" (step one of any negative Profit) would immediately
                 render back as NaN and wipe the character, making it
                 impossible to ever finish typing a negative number. */
              var raw = byMonth[d.month] || { cases: "", profit: "" };
              return (
                <tr key={d.month} style={{ borderBottom: "1px solid var(--color-border)" }}>
                  <td style={histTd}>{t.months[d.month - 1]} {d.year}</td>
                  <td style={histTd}><input type="number" min={0} value={raw.cases === null || raw.cases === undefined ? "" : raw.cases} onChange={function (e) { updateRow(d.month, "cases", e.target.value); }} style={histInp} /></td>
                  <td style={histTd}><input type="number" value={raw.profit === null || raw.profit === undefined ? "" : raw.profit} onChange={function (e) { updateRow(d.month, "profit", e.target.value); }} style={histInp} /></td>
                  <td style={histTdR}>{fmtOrDash(d.acumulado)}</td>
                  <td style={histTdR}>{fmtOrDash(d.profitBox)}</td>
                  <td style={histTdR}>{fmtOrDash(d.profitDay)}</td>
                  <td style={histTdR}>{fmtOrDash(d.profitHour)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/* ===== FORM ===== */
function SeasonForm({ season, seasons, t, onSave, onDelete, onCancel }) {
  const isEdit = !!season;
  const [form, setForm] = useState(isEdit ? { ...season } : { ...EMPTY });
  const [errors, setErrors] = useState({}); const [showDel, setShowDel] = useState(false); const [submitted, setSubmitted] = useState(false); const [showExit, setShowExit] = useState(false);
  /* Historical Monthly tab only exists for Closed seasons — Open ones
     compute cases/profit live from shipments, so a manual-capture tab
     would just confuse the user with something to fill in that's
     already automatic. */
  const [activeTab, setActiveTab] = useState("details");
  const showHistoricalTab = form.status === "closed";
  const set = (k, v) => { setForm(f => ({ ...f, [k]: v })); if (errors[k]) setErrors(e => ({ ...e, [k]: null })); };
  const tryCancel = () => { const orig = isEdit ? JSON.stringify(season) : JSON.stringify(EMPTY); if (JSON.stringify(form) !== orig) setShowExit(true); else onCancel(); };

  const name = genName(form.startDate, form.endEstimate);
  const weeks = calcWeeks(form.startDate, form.endEstimate);
  const weeklyTarget = weeks > 0 && form.profitGoal ? form.profitGoal / weeks : 0;

  const validate = () => {
    const e = {};
    if (!form.startDate) e.startDate = t.required;
    if (!form.endEstimate) e.endEstimate = t.required;
    if (form.profitGoal === "" || form.profitGoal === null || form.profitGoal === undefined) e.profitGoal = t.required;
    // Check no overlap
    if (form.startDate && form.endEstimate) {
      const s = new Date(form.startDate); const en = new Date(form.endEstimate);
      if (en <= s) e.endEstimate = t.required;
      const overlap = seasons.find(ss => {
        if (ss.id === (season?.id)) return false;
        const os = new Date(ss.startDate); const oe = new Date(ss.endEstimate);
        return s < oe && en > os;
      });
      if (overlap) e.startDate = t.overlapError;
    }
    // Check no other open season (for new seasons)
    if (!isEdit && form.status === "open") {
      const hasOpen = seasons.find(s => s.status === "open" || s.status === "reOpened");
      if (hasOpen) e.startDate = t.alreadyOpen;
    }
    setErrors(e); return Object.keys(e).length === 0;
  };
  const handleSave = () => {
    setSubmitted(true);
    if (!validate()) return;
    /* Inyectar seasonName al guardar — el form no lo captura, se
       deriva de startDate + endEstimate vía genName. La columna
       season_name en Supabase es NOT NULL, sin esto el save falla. */
    onSave({
      ...form,
      seasonName: genName(form.startDate, form.endEstimate),
      profitGoal: Number(form.profitGoal),
      id: isEdit ? form.id : Date.now(),
    });
  };
  const eb = k => errors[k] ? { borderColor: "var(--color-danger)" } : {};

  return (
    <DraggableModal title={(isEdit ? t.edit : t.add) + (name ? "  ·  " + name : "")} onClose={tryCancel} initialWidth={showHistoricalTab ? 760 : 540} initialHeight={Math.min(700, window.innerHeight - 60)}>
      <div style={{ overflow: "auto", flex: 1 }}>
        {showExit && (<div style={{ padding: "12px 22px", background: "rgba(212,148,10,0.08)", borderBottom: "1px solid rgba(212,148,10,0.2)", display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 8 }}><span style={{ fontSize: 12, color: "var(--color-warning)", fontWeight: 500 }}>{t.confirmExit}</span><div style={{ display: "flex", gap: 6 }}><button onClick={onCancel} style={{ padding: "5px 10px", borderRadius: 4, border: "none", background: "var(--color-warning)", color: "#fff", cursor: "pointer", fontSize: 11, fontWeight: 600 }}>{t.yesExit}</button><button onClick={() => setShowExit(false)} style={{ padding: "5px 10px", borderRadius: 4, border: "1px solid var(--color-border)", background: "transparent", color: "var(--color-text-secondary)", cursor: "pointer", fontSize: 11 }}>{t.noExit}</button></div></div>)}
        {showHistoricalTab && (
          <div style={{ display: "flex", gap: 4, padding: "10px 22px 0" }}>
            {["details", "historical"].map(function (tab) {
              var active = activeTab === tab;
              return (
                <button key={tab} onClick={() => setActiveTab(tab)} style={{ padding: "8px 16px", borderRadius: "6px 6px 0 0", border: "1px solid var(--color-border)", borderBottom: active ? "1px solid var(--color-bg-card-solid)" : "1px solid var(--color-border)", background: active ? "var(--color-bg-card-solid)" : "var(--color-bg)", color: active ? "var(--color-accent)" : "var(--color-text-secondary)", cursor: "pointer", fontSize: 12, fontWeight: 600, marginBottom: -1, position: "relative", zIndex: active ? 1 : 0 }}>
                  {tab === "details" ? t.tabDetails : t.tabHistorical}
                </button>
              );
            })}
          </div>
        )}
        {(!showHistoricalTab || activeTab === "details") && (
        <div style={{ padding: "18px 22px", display: "flex", flexDirection: "column", gap: 18, borderTop: showHistoricalTab ? "1px solid var(--color-border)" : "none" }}>
          {submitted && Object.keys(errors).length > 0 && (<div style={{ background: "rgba(231,76,60,0.08)", border: "1px solid rgba(231,76,60,0.2)", borderRadius: 6, padding: "8px 12px", fontSize: 12, color: "var(--color-danger)", fontWeight: 500 }}>{t.fillRequired}</div>)}

          {/* Status is only pickable at CREATE time. Editing an existing
              season's status still goes exclusively through SeasonDetail's
              preClose/close/reOpen action buttons — unchanged. Defaults to
              Closed (see EMPTY) since most new-season creations now are
              historical backfills; picking Open here re-triggers the
              existing "only one Open at a time" check in validate(). */}
          {!isEdit && (
            <Section title={t.status}>
              <Field label={t.status}>
                <SearchSelect
                  value={form.status}
                  onChange={v => set("status", v)}
                  options={[{ value: "closed", label: t.closed }, { value: "open", label: t.open }]}
                />
              </Field>
            </Section>
          )}

          <Section title={t.startDate}>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
              <Field label={t.startDate} required error={errors.startDate}><input type="date" value={form.startDate} onChange={e => set("startDate", e.target.value)} style={{ ...inp, ...eb("startDate") }} /></Field>
              <Field label={t.endEstimate} required error={errors.endEstimate} sub={t.endEstimateSub}><input type="date" value={form.endEstimate} onChange={e => set("endEstimate", e.target.value)} style={{ ...inp, ...eb("endEstimate") }} /></Field>
            </div>
          </Section>

          {weeks > 0 && <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 10 }}>
            <div style={{ textAlign: "center", background: "var(--color-bg)", borderRadius: 10, padding: "10px 6px" }}>
              <div style={{ fontFamily: "'JetBrains Mono', monospace", fontWeight: 700, fontSize: 20, color: "var(--color-accent)" }}>{weeks}</div>
              <div style={{ fontSize: 10, color: "var(--color-text-muted)" }}>{t.weeks}</div>
            </div>
            <div style={{ textAlign: "center", background: "var(--color-bg)", borderRadius: 10, padding: "10px 6px" }}>
              <div style={{ fontFamily: "'JetBrains Mono', monospace", fontWeight: 600, fontSize: 14, color: "var(--color-text)" }}>W{getWeekNum(form.startDate)} → W{getWeekNum(form.endEstimate)}</div>
              <div style={{ fontSize: 10, color: "var(--color-text-muted)" }}>{t.startWeek} → {t.endWeek}</div>
            </div>
            <div style={{ textAlign: "center", background: "var(--color-bg)", borderRadius: 10, padding: "10px 6px" }}>
              <div style={{ fontFamily: "'JetBrains Mono', monospace", fontWeight: 700, fontSize: 14, color: "var(--color-text)" }}>{fmtMoney(weeklyTarget)}</div>
              <div style={{ fontSize: 10, color: "var(--color-text-muted)" }}>{t.weeklyTarget}</div>
            </div>
          </div>}

          <Section title={t.profitGoal}>
            <Field label={t.profitGoal} required error={errors.profitGoal} sub={t.profitGoalSub}>
              <div style={{ position: "relative" }}>
                <span style={{ position: "absolute", left: 10, top: "50%", transform: "translateY(-50%)", fontSize: 14, fontWeight: 600, color: "var(--color-text-muted)" }}>$</span>
                <input type="number" min={0} step={1000} placeholder="250000" value={form.profitGoal} onChange={e => set("profitGoal", e.target.value)} style={{ ...inp, paddingLeft: 24, fontFamily: "'JetBrains Mono', monospace", fontWeight: 600, fontSize: 16, ...eb("profitGoal") }} />
              </div>
            </Field>
          </Section>
        </div>
        )}
        {showHistoricalTab && activeTab === "historical" && (
          <div style={{ padding: "18px 22px", borderTop: "1px solid var(--color-border)" }}>
            <HistoricalMonthlyTab form={form} set={set} t={t} />
          </div>
        )}

        <div style={{ padding: "14px 22px 18px", borderTop: "1px solid var(--color-border)", display: "flex", justifyContent: "space-between", alignItems: "center", position: "sticky", bottom: 0, background: "var(--color-bg-card-solid)", borderRadius: "0 0 16px 16px" }}>
          <div>{isEdit && !showDel && season.status !== "closed" && <button onClick={() => setShowDel(true)} style={{ padding: "8px 14px", borderRadius: 6, border: "1px solid rgba(231,76,60,0.25)", background: "rgba(231,76,60,0.08)", color: "var(--color-danger)", cursor: "pointer", fontSize: 12, fontWeight: 500 }}>{t.delete}</button>}{showDel && (<div style={{ display: "flex", alignItems: "center", gap: 8 }}><span style={{ fontSize: 12, color: "var(--color-danger)" }}>{t.confirmDel}</span><button onClick={() => onDelete(season.id)} style={{ padding: "6px 10px", borderRadius: 4, border: "none", background: "var(--color-danger)", color: "#fff", cursor: "pointer", fontSize: 11, fontWeight: 600 }}>{t.yes}</button><button onClick={() => setShowDel(false)} style={{ padding: "6px 10px", borderRadius: 4, border: "1px solid var(--color-border)", background: "transparent", color: "var(--color-text-secondary)", cursor: "pointer", fontSize: 11 }}>{t.no}</button></div>)}</div>
          <div style={{ display: "flex", gap: 8 }}><button onClick={tryCancel} style={{ padding: "8px 16px", borderRadius: 6, border: "1px solid var(--color-border)", background: "transparent", color: "var(--color-text-secondary)", cursor: "pointer", fontSize: 13, fontWeight: 500 }}>{t.cancel}</button><button onClick={handleSave} style={{ padding: "8px 20px", borderRadius: 6, border: "none", background: "var(--color-accent)", color: "var(--color-accent-text)", cursor: "pointer", fontSize: 13, fontWeight: 600 }}>{t.save}</button></div>
        </div>
      </div>
    </DraggableModal>
  );
}

/* ===== MAIN ===== */
export default function SeasonRegistry({ lang: globalLang }) {
  const lang = globalLang || "es"; const t = T[lang];
  const [seasons, setSeasons] = useState([]);
  const [search, setSearch] = useState(""); const [statusFilter, setStatusFilter] = useState("all");
  const [seasonFilter, setSeasonFilter] = useState([]);
  const [dateFrom, setDateFrom] = useState(""); const [dateTo, setDateTo] = useState("");
  const [sortCol, setSortCol] = useState(() => { try { const s = JSON.parse(localStorage.getItem("gfi-seasons-sort") || "null"); return s && s.column ? s.column : null; } catch { return null; } });
  const [sortDir, setSortDir] = useState(() => { try { const s = JSON.parse(localStorage.getItem("gfi-seasons-sort") || "null"); return s && s.direction ? s.direction : null; } catch { return null; } });
  useEffect(() => { try { localStorage.setItem("gfi-seasons-sort", JSON.stringify({ column: sortCol, direction: sortDir })); } catch {} }, [sortCol, sortDir]);
  /* Brushed Steel redesign (ported from ShipmentRegistry.js/
     BillRegistry.js/InvoiceRegistry.js) — Filters popover (Season/
     Date range) and a "⋮" more-actions kebab (Export Excel), same
     anchored-popover pattern as the other converted list modules. */
  const [showFilters, setShowFilters] = useState(false);
  const filtersRef = useRef(null);
  const [showMoreMenu, setShowMoreMenu] = useState(false);
  const moreMenuRef = useRef(null);
  useEffect(() => {
    if (!showFilters) return;
    const onDown = (e) => { if (filtersRef.current && !filtersRef.current.contains(e.target)) setShowFilters(false); };
    const onKey = (e) => { if (e.key === "Escape") setShowFilters(false); };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => { document.removeEventListener("mousedown", onDown); document.removeEventListener("keydown", onKey); };
  }, [showFilters]);
  useEffect(() => {
    if (!showMoreMenu) return;
    const onDown = (e) => { if (moreMenuRef.current && !moreMenuRef.current.contains(e.target)) setShowMoreMenu(false); };
    const onKey = (e) => { if (e.key === "Escape") setShowMoreMenu(false); };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => { document.removeEventListener("mousedown", onDown); document.removeEventListener("keydown", onKey); };
  }, [showMoreMenu]);
  const [viewing, setViewing] = useState(null); const [editing, setEditing] = useState(null); const [showForm, setShowForm] = useState(false);
  const [toast, setToast] = useState(null); const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState({});
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  const aliveRef = useRef(true);

  /* Source of truth = Supabase `seasons`. We ALSO write through to
     localStorage on every save/delete so the exported `calcSeasonProfits()`
     (consumed by Dashboard and ProfitTracker which haven't been
     migrated yet) keeps returning correct data sync. Drop the dual
     write once those callers migrate. */
  const writeSeasonsCache = (next) => {
    try { localStorage.setItem("gfi-seasons", JSON.stringify(next)); } catch (e) {}
  };
  const reload = async () => {
    setError(null); setLoading(true);
    try {
      const rows = await loadFromSupabase("seasons", { force: true });
      if (aliveRef.current) { setSeasons(rows); writeSeasonsCache(rows); }
    } catch (e) {
      if (aliveRef.current) setError("Could not load seasons: " + (e.message || String(e)));
    } finally {
      if (aliveRef.current) setLoading(false);
    }
  };
  useEffect(() => {
    aliveRef.current = true;
    setError(null); setLoading(true);
    loadFromSupabase("seasons")
      .then(rows => { if (aliveRef.current) { setSeasons(rows); writeSeasonsCache(rows); } })
      .catch(e => { if (aliveRef.current) setError("Could not load seasons: " + (e.message || String(e))); })
      .finally(() => { if (aliveRef.current) setLoading(false); });
    return () => { aliveRef.current = false; };
  }, []);
  const [seasonProfits, setSeasonProfits] = useState({});
  useEffect(() => { setSeasonProfits(calcSeasonProfits()); }, []);
  /* Refresh profits when returning to this module */
  useEffect(() => { const r = () => setSeasonProfits(calcSeasonProfits()); window.addEventListener("focus", r); return () => window.removeEventListener("focus", r); }, []);

  const handleSave = async s => {
    if (saving) return;
    setSaving(true); setError(null);
    try {
      const saved = await saveToSupabase("seasons", s);
      const ex = seasons.find(x => x.id === s.id);
      const next = ex ? seasons.map(x => x.id === s.id ? saved : x) : seasons.concat([saved]);
      setSeasons(next); writeSeasonsCache(next);
      setShowForm(false); setEditing(null); setViewing(null);
      setToast(t.saved);
    } catch (e) {
      setError("Could not save season: " + (e.message || String(e)));
      setToast("⚠ " + e.message);
    } finally { setSaving(false); }
  };
  const handleDelete = async id => {
    if (saving) return;
    setSaving(true); setError(null);
    try {
      await deleteFromSupabase("seasons", id);
      const next = seasons.filter(s => s.id !== id);
      setSeasons(next); writeSeasonsCache(next);
      setShowForm(false); setEditing(null); setViewing(null);
      setToast(t.deleted);
    } catch (e) {
      setError("Could not delete season: " + (e.message || String(e)));
      setToast("⚠ " + e.message);
    } finally { setSaving(false); }
  };
  const handleEdit = s => { setViewing(null); setEditing(s); setShowForm(true); };
  const handleSort = col => { if (sortCol === col) { if (sortDir === "asc") setSortDir("desc"); else { setSortCol(null); setSortDir(null); } } else { setSortCol(col); setSortDir("asc"); } };
  const handleStatusChange = async (id, newStatus) => {
    if (saving) return;
    const now = new Date().toISOString().slice(0, 10);
    const target = seasons.find(s => s.id === id);
    if (!target) return;
    const patched = { ...target, status: newStatus };
    if (newStatus === "preClose") patched.preCloseDate = now;
    if (newStatus === "closed") patched.closeDate = now;
    if (newStatus === "reOpened") { patched.closeDate = ""; patched.preCloseDate = ""; }
    setSaving(true); setError(null);
    try {
      const saved = await saveToSupabase("seasons", patched);
      const next = seasons.map(s => s.id === id ? saved : s);
      setSeasons(next); writeSeasonsCache(next);
      setViewing(prev => (prev && prev.id === id ? saved : prev));
      setToast(t.saved);
    } catch (e) {
      setError("Could not update status: " + (e.message || String(e)));
      setToast("⚠ " + e.message);
    } finally { setSaving(false); }
  };

  const filtered = seasons.filter(s => {
    if (statusFilter !== "all" && s.status !== statusFilter) return false;
    if (seasonFilter.length > 0 && !seasonFilter.includes(String(s.id))) return false;
    if (search) { const q = search.toLowerCase(); const name = genName(s.startDate, s.endEstimate).toLowerCase(); if (!name.includes(q)) return false; }
    if (dateFrom || dateTo) {
      const d = s.startDate || "";
      if (!d) return false;
      if (dateFrom && d < dateFrom) return false;
      if (dateTo && d > dateTo) return false;
    }
    return true;
  });

  const sorted = useMemo(() => {
    const arr = [...filtered];
    if (!sortCol || !sortDir) return arr.sort((a, b) => (b.startDate || "").localeCompare(a.startDate || ""));
    arr.sort((a, b) => {
      let va, vb;
      switch (sortCol) {
        case "name": va = genName(a.startDate, a.endEstimate); vb = genName(b.startDate, b.endEstimate); break;
        case "start": va = a.startDate || ""; vb = b.startDate || ""; break;
        case "week": va = a.startDate || ""; vb = b.startDate || ""; break;
        case "end": va = a.endEstimate || ""; vb = b.endEstimate || ""; break;
        case "endWeek": va = a.endEstimate || ""; vb = b.endEstimate || ""; break;
        case "weeks": va = calcWeeks(a.startDate, a.endEstimate); vb = calcWeeks(b.startDate, b.endEstimate); return sortDir === "asc" ? va - vb : vb - va;
        case "goal": va = Number(a.profitGoal) || 0; vb = Number(b.profitGoal) || 0; return sortDir === "asc" ? va - vb : vb - va;
        case "real": va = getSeasonTotals(a, seasonProfits).profit; vb = getSeasonTotals(b, seasonProfits).profit; return sortDir === "asc" ? va - vb : vb - va;
        case "weekly": { const wa = calcWeeks(a.startDate, a.endEstimate); const wb = calcWeeks(b.startDate, b.endEstimate); va = wa > 0 ? a.profitGoal / wa : 0; vb = wb > 0 ? b.profitGoal / wb : 0; return sortDir === "asc" ? va - vb : vb - va; }
        case "weeklyReal": { const wa = (seasonProfits[a.id] || {}).weekCount || 0; const wb = (seasonProfits[b.id] || {}).weekCount || 0; va = wa > 0 ? ((seasonProfits[a.id] || {}).total || 0) / wa : 0; vb = wb > 0 ? ((seasonProfits[b.id] || {}).total || 0) / wb : 0; return sortDir === "asc" ? va - vb : vb - va; }
        case "status": { const order = { open: 0, reOpened: 1, preClose: 2, closed: 3 }; va = order[a.status] ?? 4; vb = order[b.status] ?? 4; return sortDir === "asc" ? va - vb : vb - va; }
        default: return 0;
      }
      if (typeof va === "string") { const r = va.localeCompare(vb); return sortDir === "asc" ? r : -r; }
      return 0;
    });
    return arr;
  }, [filtered, sortCol, sortDir, seasonProfits]);

  /* Aggregate Profit (Real) + Cases for an arbitrary list of seasons.
     Used both for the visible-rows summary cards / TOTAL row and for the
     "X selected" bar — same denominator, different filters. */
  const aggregate = (list) => list.reduce((acc, s) => {
    const st = getSeasonTotals(s, seasonProfits);
    acc.profit += st.profit;
    acc.cases += st.cases;
    return acc;
  }, { profit: 0, cases: 0 });
  const filteredTotals = useMemo(() => aggregate(sorted), [sorted, seasonProfits]);
  const selectedTotals = useMemo(() => aggregate(seasons.filter(s => selected[s.id])), [seasons, selected, seasonProfits]);

  function SortIcon({ col }) { if (sortCol !== col) return <span style={{ color: "var(--color-border-hover)", fontSize: 10, marginLeft: 3 }}>↕</span>; return <span style={{ color: "var(--color-accent)", fontSize: 10, marginLeft: 3 }}>{sortDir === "asc" ? "↑" : "↓"}</span>; }
  /* Brushed Steel metal-plate spec (see App.css "BRUSHED STEEL" /
     ShipmentRegistry.js, BillRegistry.js, InvoiceRegistry.js) —
     brushed-metal gradient + 1px #b9bcc0 border, embossed text (dark
     #1a2636 fill, light-below/dark-above text-shadow), square
     corners, 9px column headers. Background lives on the <thead><tr>
     itself (see thead render below) for one continuous bar; `thS`
     keeps only text + the shared border. */
  const thS = { textAlign: "left", padding: "7px 5px", fontWeight: 700, fontSize: 9, color: "#1a2636", textShadow: "0 1px 0 rgba(255,255,255,0.8), 0 -1px 0 rgba(0,0,0,0.2)", textTransform: "uppercase", letterSpacing: "0.05em", background: "transparent", borderTop: "1px solid #b9bcc0", borderBottom: "1px solid #b9bcc0", cursor: "pointer", userSelect: "none", whiteSpace: "nowrap" };

  const allSelected = sorted.length > 0 && sorted.every(s => selected[s.id]);
  const toggleAll = () => { const ns = {}; if (!allSelected) sorted.forEach(s => { ns[s.id] = true; }); setSelected(ns); };
  const toggleOne = (id, e) => { if (e) e.stopPropagation(); setSelected(p => { const ns = { ...p }; if (ns[id]) delete ns[id]; else ns[id] = true; return ns; }); };
  const selectedCount = Object.keys(selected).length;
  const clearSelection = () => setSelected({});
  const [showBulkDelete, setShowBulkDelete] = useState(false);

  /* Excel export. Summary = one row per season with totals and goals.
     Detail = one row per (season, week) using the per-week data
     already aggregated by calcSeasonProfits — same source the table
     uses, no recomputation. */
  const seasonsToExport = () => {
    const sel = sorted.filter(ss => selected[ss.id]);
    return sel.length > 0 ? sel : sorted;
  };
  const buildSeasonSummaryRows = () => {
    return seasonsToExport().map(ss => {
      const st = getSeasonTotals(ss, seasonProfits);
      const weeks = calcWeeks(ss.startDate, ss.endEstimate);
      return {
        Season: genName(ss.startDate, ss.endEstimate),
        Status: xs(ss.status),
        "Start Date": xs(ss.startDate),
        "End Estimate": xs(ss.endEstimate),
        "Pre-Close Date": xs(ss.preCloseDate),
        "Close Date": xs(ss.closeDate),
        Weeks: intOr(weeks),
        "Profit Goal": num2(ss.profitGoal),
        "Profit Real": num2(st.profit),
        "Weekly Goal": num2(weeks > 0 ? (parseFloat(ss.profitGoal) || 0) / weeks : 0),
      };
    });
  };
  const buildSeasonDetailRows = () => {
    const out = [];
    seasonsToExport().forEach(ss => {
      const meta = { Season: genName(ss.startDate, ss.endEstimate), Status: xs(ss.status) };
      const st = getSeasonTotals(ss, seasonProfits);
      const weekRows = st.weekDetail;
      if (weekRows.length === 0) {
        out.push({ ...meta, Type: "Summary", Week: "", Cases: st.cases || "", Profit: num2(st.profit) });
        return;
      }
      weekRows.forEach(w => {
        out.push({ ...meta, Type: "Week", Week: intOr(w.week), Cases: intOr(w.cases), Profit: num2(w.profit) });
      });
    });
    return out;
  };
  const handleBulkDelete = async () => {
    if (saving) return;
    setSaving(true); setError(null);
    const ids = Object.keys(selected);
    try {
      await Promise.all(ids.map(id => deleteFromSupabase("seasons", id)));
      const keep = seasons.filter(s => !selected[s.id]);
      setSeasons(keep); writeSeasonsCache(keep);
      setShowBulkDelete(false); setSelected({}); setToast(t.deleted);
    } catch (e) {
      setError("Could not bulk delete: " + (e.message || String(e)));
      setToast("⚠ " + e.message);
    } finally { setSaving(false); }
  };

  if (loading) return <div style={{ display: "flex", alignItems: "center", justifyContent: "center", minHeight: 200, color: "var(--color-text-muted)" }}>Loading...</div>;

  /* Brushed Steel action button / dark-silver "title plate" variant —
     same spec as ShipmentRegistry.js/BillRegistry.js/InvoiceRegistry.js's
     list toolbar (square corners, brushed-metal gradient, embossed
     text). */
  var actBtn = { padding: "5px 12px", borderRadius: 0, border: "1px solid #b9bcc0", background: "linear-gradient(180deg, #f6f7f8 0%, #d8dadd 100%)", boxShadow: "inset 0 1px 0 rgba(255,255,255,0.9), inset 0 -1px 0 rgba(0,0,0,0.08), 0 1px 2px rgba(0,0,0,0.15)", color: "#1a2636", textShadow: "0 1px 0 rgba(255,255,255,0.8), 0 -1px 0 rgba(0,0,0,0.2)", cursor: "pointer", fontSize: 12, fontWeight: 600 };
  var actBtnDanger = Object.assign({}, actBtn, { borderColor: "rgba(220,38,38,0.4)", color: "#B91C1C", textShadow: "0 1px 0 rgba(255,255,255,0.8), 0 -1px 0 rgba(0,0,0,0.1)" });
  var titlePlateBtn = { padding: "5px 12px", borderRadius: 0, border: "1px solid #7d838c", background: "linear-gradient(180deg, #c7cbd1 0%, #9aa0a8 100%)", boxShadow: "inset 0 1px 0 rgba(255,255,255,0.7), inset 0 -1px 0 rgba(0,0,0,0.15)", color: "#000000", textShadow: "0 1px 0 rgba(255,255,255,0.8), 0 -1px 0 rgba(0,0,0,0.25)", cursor: "pointer", fontSize: 12, fontWeight: 600 };
  var totalsLabel = { textAlign: "right", fontSize: 9, color: "#1a2636", textShadow: "0 1px 0 rgba(255,255,255,0.7), 0 -1px 0 rgba(0,0,0,0.15)", whiteSpace: "nowrap" };
  var totalsValue = { textAlign: "right", fontSize: 10, color: "#000000", textShadow: "0 1px 0 rgba(255,255,255,0.8), 0 -1px 0 rgba(0,0,0,0.25)", fontWeight: 600, whiteSpace: "nowrap" };

  /* Status tabs (All/Open/Pre-close/Closed/Re-opened) — replaces the
     old statusFilter dropdown, same inset/pressed-segment pattern as
     Shipments/Bills/Invoices' tabs row. */
  var tabsCfg = [
    { key: "all", label: lang === "es" ? "Todas" : "All" },
    { key: "open", label: t.open },
    { key: "preClose", label: t.preClose },
    { key: "closed", label: t.closed },
    { key: "reOpened", label: t.reOpened },
  ];
  var activeFilterCount = (seasonFilter.length > 0 ? 1 : 0) + (dateFrom ? 1 : 0) + (dateTo ? 1 : 0);
  var clearAllFilters = () => { setSeasonFilter([]); setDateFrom(""); setDateTo(""); };

  return (
    <div>
      {/* HEADER ROW — title (left); search + filters + kebab + new
          (right); module totals plate on the same row as the action
          group. Brushed Steel metal-plate treatment ported from
          ShipmentRegistry.js/BillRegistry.js/InvoiceRegistry.js's
          list header. */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 6, gap: 24, flexWrap: "wrap" }}>
        {/* Title + "showing N of M" wrapped in the same dark-silver
            title-bar plate as the table's header bar, black embossed
            text. */}
        <div style={{ lineHeight: 1.1, padding: "8px 14px", background: "linear-gradient(180deg, #c7cbd1 0%, #9aa0a8 100%)", border: "1px solid #7d838c", boxShadow: "inset 0 1px 0 rgba(255,255,255,0.7), inset 0 -1px 0 rgba(0,0,0,0.15)" }}>
          <h1 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: "#000000", textShadow: "0 1px 0 rgba(255,255,255,0.8), 0 -1px 0 rgba(0,0,0,0.25)" }}>{t.title}</h1>
          <p style={{ margin: 0, fontSize: 10, color: "#1a2636", textShadow: "0 1px 0 rgba(255,255,255,0.7), 0 -1px 0 rgba(0,0,0,0.15)" }}>{t.showing} {sorted.length} {t.of} {seasons.length} {t.total}</p>
        </div>
        <div style={{ display: "flex", alignItems: "flex-start", gap: 24, flexWrap: "wrap" }}>
          {/* Action group: search + filters + kebab (Export Excel) +
              New season. */}
          <div style={{ display: "flex", alignItems: "center", gap: 8, position: "relative" }} ref={filtersRef}>
            {/* SEARCH — neumorphic white pill with an embossed
                magnifier icon. */}
            <span style={{ position: "relative", display: "inline-block" }}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#ffffff" strokeWidth="2.5" strokeLinecap="round" style={{ position: "absolute", left: 10, top: "50%", transform: "translateY(-50%)", pointerEvents: "none", filter: "drop-shadow(0 1px 0 rgba(255,255,255,0.9)) drop-shadow(0 -1px 1px rgba(0,0,0,0.3))" }}>
                <circle cx="11" cy="11" r="7" /><line x1="21" y1="21" x2="16.65" y2="16.65" />
              </svg>
              <input
                type="text"
                value={search}
                onChange={e => setSearch(e.target.value)}
                placeholder={t.search}
                style={{ width: 200, padding: "6px 10px 6px 28px", fontSize: 12, borderRadius: 999, border: "none", background: "#ffffff", boxShadow: "inset 1px 1px 3px rgba(0,0,0,0.15), inset -1px -1px 2px rgba(255,255,255,0.8), 0 1px 1px rgba(255,255,255,0.9), 0 -1px 1px rgba(0,0,0,0.1)", outline: "none", fontFamily: "inherit", color: "var(--color-text-secondary)" }}
              />
            </span>
            {search && (
              <button type="button" onClick={() => setSearch("")} style={{ padding: "5px 10px", borderRadius: 5, border: "1px solid var(--color-border)", background: "transparent", color: "var(--color-text-secondary)", cursor: "pointer", fontSize: 11 }} title={lang === "es" ? "Limpiar búsqueda" : "Clear search"}>×</button>
            )}
            <button
              type="button"
              onClick={() => setShowFilters(v => !v)}
              style={Object.assign({}, titlePlateBtn, { display: "flex", alignItems: "center", gap: 6, padding: "5px 10px" })}
              title={lang === "es" ? "Filtros" : "Filters"}
            >
              <span style={{ fontSize: 16, lineHeight: 1, letterSpacing: 0 }}>⋮</span>
              <span>{lang === "es" ? "Filtros" : "Filters"}</span>
              {activeFilterCount > 0 && (
                <span style={{ minWidth: 18, height: 18, padding: "0 5px", borderRadius: 9, background: "var(--color-accent)", color: "var(--color-accent-text)", fontSize: 10, fontWeight: 700, lineHeight: "18px", textAlign: "center", marginLeft: 2 }}>{activeFilterCount}</span>
              )}
            </button>
            {/* KEBAB — document/export actions (Export Excel). Reused
                as-is, never re-implemented. */}
            <span style={{ position: "relative", display: "inline-block" }} ref={moreMenuRef}>
              <button
                type="button"
                onClick={() => setShowMoreMenu(v => !v)}
                style={Object.assign({}, titlePlateBtn, { display: "flex", alignItems: "center", padding: "5px 10px" })}
                title={lang === "es" ? "Más acciones" : "More actions"}
              >
                <span style={{ fontSize: 16, lineHeight: 1, letterSpacing: 0 }}>⋮</span>
              </button>
              {showMoreMenu && (
                <div style={{ position: "absolute", top: "100%", left: 0, marginTop: 4, background: "linear-gradient(180deg, #c7cbd1 0%, #9aa0a8 100%)", border: "1px solid #7d838c", borderRadius: 0, boxShadow: "inset 0 1px 0 rgba(255,255,255,0.7), inset 0 -1px 0 rgba(0,0,0,0.15), 0 8px 24px rgba(0,0,0,0.18)", zIndex: 50, minWidth: 180, overflow: "visible", padding: 4 }}>
                  <div style={{ padding: "4px 10px" }}>
                    <ExportButton lang={lang} filenameBase="Seasons" selectedCount={selectedCount} totalCount={sorted.length} buildSummary={buildSeasonSummaryRows} buildDetail={buildSeasonDetailRows} style={{ width: "100%" }} btnStyle={{ display: "flex", alignItems: "center", gap: 8, width: "100%", padding: "8px 10px", border: "none", borderRadius: 0, background: "transparent", color: "#000000", textShadow: "0 1px 0 rgba(255,255,255,0.7), 0 -1px 0 rgba(0,0,0,0.15)", fontSize: 12, fontWeight: 400 }} />
                  </div>
                </div>
              )}
            </span>
            {/* + New season — Brushed Steel's "colored/active plate"
                variant (same blue gradient used for Save/active toggles
                elsewhere in this design), square corners. */}
            <button onClick={() => { setEditing(null); setShowForm(true); }} style={{ padding: "6px 14px", borderRadius: 0, border: "1px solid #1a3550", background: "linear-gradient(180deg, #6FA3C7 0%, #3E6E96 55%, #284d6b 100%)", boxShadow: "inset 0 1px 1px rgba(255,255,255,0.3), inset 0 -1px 2px rgba(0,0,0,0.2), 0 1px 2px rgba(0,0,0,0.15)", color: "#ffffff", textShadow: "0 1px 1px rgba(0,0,0,0.25)", cursor: "pointer", fontSize: 12, fontWeight: 700, display: "flex", alignItems: "center", gap: 5 }}><span style={{ fontSize: 14, lineHeight: 1 }}>+</span>{t.add}</button>
            {/* FILTER POPOVER — absolute under the trigger row, same
                dark-silver plate as the title plate; fields inside
                (SearchSelect, date inputs) stay their own recessed
                plates sitting on it. */}
            {showFilters && (
              <div style={{ position: "absolute", top: "100%", right: 0, marginTop: 8, zIndex: 50, width: 440, maxWidth: "94vw", background: "linear-gradient(180deg, #c7cbd1 0%, #9aa0a8 100%)", border: "1px solid #7d838c", borderRadius: 0, boxShadow: "inset 0 1px 0 rgba(255,255,255,0.7), inset 0 -1px 0 rgba(0,0,0,0.15), 0 8px 24px rgba(0,0,0,0.18)", padding: 12 }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
                  <div style={{ fontSize: 12, fontWeight: 700, color: "#000000", textShadow: "0 1px 0 rgba(255,255,255,0.7), 0 -1px 0 rgba(0,0,0,0.15)" }}>{lang === "es" ? "Filtros" : "Filters"}{activeFilterCount > 0 ? " (" + activeFilterCount + ")" : ""}</div>
                  <div style={{ display: "flex", gap: 6 }}>
                    {activeFilterCount > 0 && (
                      <button type="button" onClick={clearAllFilters} style={Object.assign({}, actBtn, { padding: "4px 8px", fontSize: 10 })}>{lang === "es" ? "Limpiar todo" : "Clear all"}</button>
                    )}
                    <button type="button" onClick={() => setShowFilters(false)} style={Object.assign({}, actBtn, { padding: "4px 8px", fontSize: 10 })}>{lang === "es" ? "Cerrar" : "Close"}</button>
                  </div>
                </div>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 6 }}>
                  <div style={{ gridColumn: "1 / span 2" }}>
                    <div style={{ fontSize: 9, fontWeight: 700, color: "#1a2636", textShadow: "0 1px 0 rgba(255,255,255,0.7), 0 -1px 0 rgba(0,0,0,0.15)", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 2 }}>{t.allSeasons}</div>
                    <SearchSelect multi value={seasonFilter} onChange={v => setSeasonFilter(v)} placeholder={t.allSeasons} options={[...seasons].sort((a, b) => (b.startDate || "").localeCompare(a.startDate || "")).map(s => ({ value: String(s.id), label: genName(s.startDate, s.endEstimate) }))} style={filterFieldPlate} />
                  </div>
                  <div>
                    <div style={{ fontSize: 9, fontWeight: 700, color: "#1a2636", textShadow: "0 1px 0 rgba(255,255,255,0.7), 0 -1px 0 rgba(0,0,0,0.15)", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 2 }}>{lang === "es" ? "Desde" : "From"}</div>
                    <input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} style={Object.assign({}, inp, filterFieldPlate)} />
                  </div>
                  <div>
                    <div style={{ fontSize: 9, fontWeight: 700, color: "#1a2636", textShadow: "0 1px 0 rgba(255,255,255,0.7), 0 -1px 0 rgba(0,0,0,0.15)", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 2 }}>{lang === "es" ? "Hasta" : "To"}</div>
                    <input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} style={Object.assign({}, inp, filterFieldPlate)} />
                  </div>
                </div>
              </div>
            )}
          </div>
          {/* MODULE TOTALS — same dark title-bar silver plate as the
              header bar / title plate above. Profit and $/Box stay
              their own sign-based success/danger color (not a flat
              deduction-red) since they're the headline metric here,
              not a deduction. */}
          <div style={{ display: "grid", gridTemplateColumns: "auto auto", columnGap: 10, rowGap: 1, alignItems: "center", alignSelf: "flex-start", padding: "4px 10px", background: "linear-gradient(180deg, #c7cbd1 0%, #9aa0a8 100%)", border: "1px solid #7d838c", boxShadow: "inset 0 1px 0 rgba(255,255,255,0.7), inset 0 -1px 0 rgba(0,0,0,0.15)" }}>
            <span style={totalsLabel}>{t.totalProfit}:</span>
            <span style={{ ...totalsValue, color: filteredTotals.profit >= 0 ? "#059669" : "#DC2626", textShadow: "none" }}>{fmtMoney(filteredTotals.profit)}</span>
            <span style={totalsLabel}>{t.totalCases}:</span>
            <span style={totalsValue}>{filteredTotals.cases.toLocaleString()}</span>
            <span style={totalsLabel}>{t.profitPerBox}:</span>
            <span style={{ ...totalsValue, color: filteredTotals.cases > 0 ? (filteredTotals.profit >= 0 ? "#059669" : "#DC2626") : "#1a2636" }}>{filteredTotals.cases > 0 ? fmtMoney(filteredTotals.profit / filteredTotals.cases) : "—"}</span>
          </div>
        </div>
      </div>
      {error && <div style={{ display: "flex", alignItems: "center", gap: 12, padding: "10px 14px", borderRadius: 8, background: "rgba(220,38,38,0.08)", border: "1px solid rgba(220,38,38,0.3)", marginBottom: 12, fontSize: 12, color: "#B91C1C", fontWeight: 500 }}><span style={{ fontSize: 14 }}>⚠</span><span style={{ flex: 1, fontFamily: "'JetBrains Mono', monospace", wordBreak: "break-word" }}>{error}</span><button onClick={reload} style={{ padding: "5px 12px", borderRadius: 5, border: "1px solid rgba(220,38,38,0.3)", background: "transparent", color: "#B91C1C", cursor: "pointer", fontSize: 11, fontWeight: 600 }}>{lang === "es" ? "Reintentar" : "Retry"}</button></div>}

      {/* TABS — All / Open / Pre-close / Closed / Re-opened. Inset/
          pressed segments (no underline). Delete Selected / Clear sit
          right after the tabs, same compact Brushed Steel metal-plate
          buttons as the module totals plate above. */}
      <div style={{ display: "flex", alignItems: "center", gap: 2, marginBottom: 6, flexWrap: "wrap" }}>
        {tabsCfg.map(tab => {
          const isActive = statusFilter === tab.key;
          return (
            <button
              key={tab.key}
              type="button"
              onClick={() => setStatusFilter(tab.key)}
              style={{
                padding: "5px 12px",
                background: isActive ? "#ffffff" : "transparent",
                boxShadow: isActive ? "inset 1px 1px 2px rgba(0,0,0,0.15)" : "none",
                border: "none",
                color: "#1a2636",
                fontWeight: 500,
                fontSize: 11,
                cursor: "pointer",
                whiteSpace: "nowrap",
              }}
            >{tab.label}</button>
          );
        })}
        <div style={{ display: "flex", alignItems: "center", gap: 6, marginLeft: 10, marginBottom: 2 }}>
          <button type="button" onClick={() => setShowBulkDelete(true)} disabled={selectedCount === 0} style={Object.assign({}, actBtnDanger, { padding: "4px 9px", fontSize: 11, opacity: selectedCount === 0 ? 0.5 : 1, cursor: selectedCount === 0 ? "not-allowed" : "pointer" })}>{lang === "es" ? "Eliminar" : "Delete Selected"}</button>
          <button type="button" onClick={clearSelection} disabled={selectedCount === 0} style={Object.assign({}, actBtn, { padding: "4px 9px", fontSize: 11, opacity: selectedCount === 0 ? 0.5 : 1, cursor: selectedCount === 0 ? "not-allowed" : "pointer" })}>{lang === "es" ? "Limpiar" : "Clear"}</button>
        </div>
        {selectedCount > 0 && (
          <div style={{ marginLeft: "auto", display: "flex", gap: 14, flexWrap: "wrap", fontSize: 11, alignItems: "center", marginBottom: 2 }}>
            <span style={{ fontWeight: 700, color: "var(--color-accent)" }}>{selectedCount} {lang === "es" ? "seleccionados" : "selected"}</span>
            <span style={{ color: "var(--color-text-muted)" }}>|</span>
            <span><span style={{ color: "var(--color-text-muted)" }}>{lang === "es" ? "Ganancia" : "Profit"}: </span><span style={{ fontFamily: "'JetBrains Mono', monospace", fontWeight: 700, color: "var(--color-text)" }}>{fmtMoney(selectedTotals.profit)}</span></span>
            <span style={{ color: "var(--color-text-muted)" }}>|</span>
            <span><span style={{ color: "var(--color-text-muted)" }}>{lang === "es" ? "Cajas" : "Cases"}: </span><span style={{ fontFamily: "'JetBrains Mono', monospace", fontWeight: 700, color: "var(--color-text)" }}>{selectedTotals.cases.toLocaleString()}</span></span>
            <span style={{ color: "var(--color-text-muted)" }}>|</span>
            <span><span style={{ color: "var(--color-text-muted)" }}>{t.profitPerBox}: </span><span style={{ fontFamily: "'JetBrains Mono', monospace", fontWeight: 700, color: "var(--color-text)" }}>{selectedTotals.cases > 0 ? fmtMoney(selectedTotals.profit / selectedTotals.cases) : "—"}</span></span>
          </div>
        )}
      </div>
      {sorted.length === 0 ? (<div style={{ textAlign: "center", padding: "40px 20px", color: "var(--color-text-muted)", fontSize: 14 }}>{t.noResults}</div>) : (
        <div style={{ overflowX: "auto", borderRadius: 0, border: "1px solid #b9bcc0", background: "#ffffff" }}>
          <table className="gfi-seasons-list-table" style={{ width: "100%", borderCollapse: "collapse", tableLayout: "auto" }}>
            <thead><tr style={{ background: "linear-gradient(180deg, #eef0f2 0%, #d2d5d9 100%)", boxShadow: "inset 0 1px 0 rgba(255,255,255,0.9), inset 0 -1px 0 rgba(0,0,0,0.08)" }}>
              <th style={{ ...thS, width: 30, cursor: "default", textAlign: "center" }}><input type="checkbox" checked={allSelected} onChange={toggleAll} /></th>
              {[{ col: "name", label: t.seasonName, a: "left" }, { col: "start", label: t.startDate, a: "center" }, { col: "week", label: t.startWeek, a: "center" }, { col: "end", label: t.endEstimate, a: "center" }, { col: "endWeek", label: t.endWeek, a: "center" }, { col: "weeks", label: t.weeks, a: "center" }, { col: "goal", label: t.profitGoal, a: "right" }, { col: "real", label: t.profitReal, a: "right" }, { col: "cases", label: lang === "es" ? "Cajas" : "Cases", a: "right" }, { col: "perBox", label: "$/Box", a: "right" }, { col: "weekly", label: t.weeklyTarget, a: "right" }, { col: "weeklyReal", label: t.weeklyReal, a: "right" }, { col: "status", label: t.status, a: "center" }].map(h => (
                <th key={h.col} onClick={() => handleSort(h.col)} style={{ ...thS, textAlign: h.a }}>{h.label}<SortIcon col={h.col} /></th>
              ))}
            </tr></thead>
            <tbody>{sorted.map((s, i) => {
              const name = genName(s.startDate, s.endEstimate);
              const weeks = calcWeeks(s.startDate, s.endEstimate);
              const weeklyTarget = weeks > 0 ? s.profitGoal / weeks : 0;
              const st = getSeasonTotals(s, seasonProfits);
              const profitReal = st.profit;
              const weekCount = st.weekCount;
              const weeklyReal = weekCount > 0 ? profitReal / weekCount : 0;
              const totalCases = st.cases;
              const perBox = totalCases > 0 ? profitReal / totalCases : 0;
              const ss = STATUS_STYLES[s.status] || STATUS_STYLES.open;
              const statusLabel = t[s.status] || s.status;
              return (
                <tr key={s.id} onClick={() => setViewing(s)} style={{ cursor: "pointer", transition: "background 0.15s", borderBottom: "1px solid var(--color-border)", background: selected[s.id] ? "rgba(185,188,192,0.3)" : "transparent" }} onMouseEnter={e => e.currentTarget.style.background = selected[s.id] ? "rgba(185,188,192,0.4)" : "rgba(185,188,192,0.2)"} onMouseLeave={e => e.currentTarget.style.background = selected[s.id] ? "rgba(185,188,192,0.3)" : "transparent"}>
                  <td style={{ padding: "6px 8px", textAlign: "center" }} onClick={e => e.stopPropagation()}><input type="checkbox" checked={!!selected[s.id]} onChange={e => toggleOne(s.id, e)} /></td>
                  <td style={{ padding: "6px 8px", fontSize: 9.5, fontWeight: 600, color: "#1a2636", whiteSpace: "nowrap" }}>{name}</td>
                  <td style={{ padding: "6px 8px", fontSize: 9.5, color: "var(--color-text-secondary)", textAlign: "center" }}>{s.startDate}</td>
                  <td style={{ padding: "6px 8px", fontSize: 9.5, color: "var(--color-text-secondary)", textAlign: "center" }}>W{getWeekNum(s.startDate)}</td>
                  <td style={{ padding: "6px 8px", fontSize: 9.5, color: "var(--color-text-secondary)", textAlign: "center" }}>{s.endEstimate}</td>
                  <td style={{ padding: "6px 8px", fontSize: 9.5, color: "var(--color-text-secondary)", textAlign: "center" }}>W{getWeekNum(s.endEstimate)}</td>
                  <td style={{ padding: "6px 8px", fontSize: 9.5, fontWeight: 600, textAlign: "center" }}>{weeks}</td>
                  <td style={{ padding: "6px 8px", fontSize: 9.5, fontWeight: 600, color: "var(--color-accent)", textAlign: "right" }}>{fmtMoney(s.profitGoal)}</td>
                  <td style={{ padding: "6px 8px", fontSize: 9.5, fontWeight: 600, color: profitReal > 0 ? "#059669" : "var(--color-text-muted)", textAlign: "right" }}>{fmtMoney(profitReal)}</td>
                  <td style={{ padding: "6px 8px", fontSize: 9.5, textAlign: "right" }}>{totalCases > 0 ? totalCases.toLocaleString() : "\u2014"}</td>
                  <td style={{ padding: "6px 8px", fontSize: 9.5, fontWeight: 600, color: "var(--color-accent)", textAlign: "right" }}>{totalCases > 0 ? fmtMoney(perBox) : "\u2014"}</td>
                  <td style={{ padding: "6px 8px", fontSize: 9.5, color: "var(--color-text-secondary)", textAlign: "right" }}>{fmtMoney(weeklyTarget)}</td>
                  <td style={{ padding: "6px 8px", fontSize: 9.5, color: weeklyReal > 0 ? "#059669" : "var(--color-text-muted)", textAlign: "right" }}>{fmtMoney(weeklyReal)}</td>
                  <td style={{ padding: "6px 8px", textAlign: "center" }}><span style={{ padding: "3px 9px", borderRadius: 0, fontSize: 9.5, fontWeight: 400, background: ss.bg, color: ss.text, border: `1px solid ${ss.border}`, boxShadow: STATUS_BEVEL }}>{statusLabel}</span></td>
                </tr>
              );
            })}</tbody>
            <tfoot><tr style={{ borderTop: "2px solid #b9bcc0", background: "linear-gradient(180deg, #eef0f2 0%, #d2d5d9 100%)" }}>
              {/* Layout: 14 cols — checkbox|name|start|week|end|endWeek|weeks|goal|real|cases|$/box|weekly|weeklyReal|status.
                  TOTAL spans first 8 (checkbox..goal), then real + cases sums, then 4 empty (—). */}
              <td style={{ padding: "6px 8px", fontWeight: 700, fontSize: 9.5, color: "#1a2636", textTransform: "uppercase", letterSpacing: "0.05em" }} colSpan={8}>{t.totalRow}</td>
              <td style={{ padding: "6px 8px", fontSize: 9.5, textAlign: "right", fontWeight: 700, color: filteredTotals.profit >= 0 ? "#059669" : "#DC2626" }}>{fmtMoney(filteredTotals.profit)}</td>
              <td style={{ padding: "6px 8px", fontSize: 9.5, textAlign: "right", fontWeight: 700 }}>{filteredTotals.cases.toLocaleString()}</td>
              <td style={{ padding: "6px 8px", fontSize: 9.5, textAlign: "right", color: "var(--color-text-muted)" }}>{"—"}</td>
              <td style={{ padding: "6px 8px", fontSize: 9.5, textAlign: "right", color: "var(--color-text-muted)" }}>{"—"}</td>
              <td style={{ padding: "6px 8px", fontSize: 9.5, textAlign: "right", color: "var(--color-text-muted)" }}>{"—"}</td>
              <td style={{ padding: "6px 8px", fontSize: 9.5, textAlign: "center", color: "var(--color-text-muted)" }}>{"—"}</td>
            </tr></tfoot>
          </table>
        </div>
      )}

      {viewing && (function() {
        const st = getSeasonTotals(viewing, seasonProfits);
        return <SeasonDetail season={{ ...viewing, profitReal: st.profit, weekDetail: st.weekDetail, weekCount: st.weekCount, casesTotal: st.cases }} t={t} lang={lang} onEdit={handleEdit} onClose={() => setViewing(null)} onStatusChange={handleStatusChange} />;
      })()}
      {showForm && <SeasonForm season={editing} seasons={seasons} t={t} onSave={handleSave} onDelete={handleDelete} onCancel={() => { setShowForm(false); setEditing(null); }} />}
      {showBulkDelete && <BulkDeleteDialog count={selectedCount} lang={lang} onCancel={() => setShowBulkDelete(false)} onConfirm={handleBulkDelete} />}
      {toast && <Toast message={toast} onClose={() => setToast(null)} />}
    </div>
  );
}